type Article = {
  slug: string;
  title: string;
  summary: string;
  html: string;
  published: string;
};

const ARTICLES: Article[] = [
  {
    slug: 'gspro-protee-open-api-reference',
    title: 'GSPro & ProTee VX Open API Reference',
    summary: 'A complete technical reference for the TCP/JSON Open API that connects GSPro with ProTee VX and other launch monitors — covering connection setup, message schemas, every field, response codes, and integration patterns.',
    published: '2026-02-27',
    html: `
      <h2>Overview</h2>
      <p>GSPro exposes a lightweight TCP socket API — commonly called the <strong>GSPro Open API</strong> — that allows any launch monitor, third-party tool, or custom adapter to communicate with the simulator in real time. ProTee VX is one of the primary launch monitors that targets this API. The protocol is JSON over a persistent TCP connection; no HTTP, no WebSockets, no authentication layer. Both sides speak plain newline-terminated JSON objects.</p>
      <p>This document covers the full wire protocol: connection setup, the two message directions, every documented field, response codes, unit conventions, and integration patterns specific to ProTee VX.</p>

      <h2>Connection</h2>
      <p>By default GSPro listens on <code>localhost:921</code>. The launch monitor (or adapter) is always the TCP client; GSPro is the server. The connection is persistent for the duration of a round — do not reconnect per shot. GSPro sends an acknowledgement message immediately on connect (code <code>200</code>) so you can confirm the link is live before sending shot data.</p>
      <ul>
        <li><strong>Host:</strong> <code>127.0.0.1</code> (local machine) or the LAN IP of the GSPro machine</li>
        <li><strong>Port:</strong> <code>921</code></li>
        <li><strong>Transport:</strong> TCP, persistent connection</li>
        <li><strong>Framing:</strong> each JSON object is terminated by a newline character (<code>\n</code>); read line-by-line</li>
        <li><strong>Encoding:</strong> UTF-8</li>
      </ul>

      <h2>Message Direction: Launch Monitor → GSPro</h2>
      <p>The launch monitor sends a JSON object for every shot (and optionally a periodic heartbeat). The top-level fields are:</p>

      <h3>Top-level fields</h3>
      <table class="api-table">
        <thead><tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>DeviceID</code></td><td>string</td><td>Yes</td><td>Identifier for the launch monitor, e.g. <code>"ProTee VX"</code></td></tr>
          <tr><td><code>Units</code></td><td>string</td><td>Yes</td><td><code>"Yards"</code> or <code>"Meters"</code> — applies to all distance/speed fields in this message</td></tr>
          <tr><td><code>ShotNumber</code></td><td>integer</td><td>Yes</td><td>Monotonically increasing shot counter; GSPro echoes it back in the response so you can correlate acknowledgements</td></tr>
          <tr><td><code>APIversion</code></td><td>string</td><td>Yes</td><td>Must be <code>"1"</code> for the current protocol version</td></tr>
          <tr><td><code>BallData</code></td><td>object</td><td>Conditional</td><td>Required when <code>ShotDataOptions.ContainsBallData</code> is <code>true</code></td></tr>
          <tr><td><code>ClubData</code></td><td>object</td><td>Conditional</td><td>Required when <code>ShotDataOptions.ContainsClubData</code> is <code>true</code></td></tr>
          <tr><td><code>ShotDataOptions</code></td><td>object</td><td>Yes</td><td>Flags that describe what is in this message and the current monitor state</td></tr>
        </tbody>
      </table>

      <h3>BallData fields</h3>
      <table class="api-table">
        <thead><tr><th>Field</th><th>Type</th><th>Unit</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>Speed</code></td><td>float</td><td>mph or m/s</td><td>Ball speed off the face</td></tr>
          <tr><td><code>SpinAxis</code></td><td>float</td><td>degrees</td><td>Spin axis tilt; negative = draw spin (tilts left), positive = fade spin (tilts right)</td></tr>
          <tr><td><code>TotalSpin</code></td><td>float</td><td>rpm</td><td>Total spin rate of the ball</td></tr>
          <tr><td><code>BackSpin</code></td><td>float</td><td>rpm</td><td>Backspin component (always positive)</td></tr>
          <tr><td><code>SideSpin</code></td><td>float</td><td>rpm</td><td>Sidespin component; negative = draw, positive = fade</td></tr>
          <tr><td><code>HLA</code></td><td>float</td><td>degrees</td><td>Horizontal Launch Angle; negative = left of target, positive = right</td></tr>
          <tr><td><code>VLA</code></td><td>float</td><td>degrees</td><td>Vertical Launch Angle (launch angle above ground)</td></tr>
        </tbody>
      </table>

      <h3>ClubData fields</h3>
      <table class="api-table">
        <thead><tr><th>Field</th><th>Type</th><th>Unit</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>Speed</code></td><td>float</td><td>mph or m/s</td><td>Club head speed at impact</td></tr>
          <tr><td><code>AngleOfAttack</code></td><td>float</td><td>degrees</td><td>Vertical swing path angle at impact; negative = descending blow</td></tr>
          <tr><td><code>FaceToTarget</code></td><td>float</td><td>degrees</td><td>Club face angle relative to target line; negative = closed, positive = open</td></tr>
          <tr><td><code>Lie</code></td><td>float</td><td>degrees</td><td>Lie angle deviation at impact</td></tr>
          <tr><td><code>Loft</code></td><td>float</td><td>degrees</td><td>Dynamic loft at impact</td></tr>
          <tr><td><code>Path</code></td><td>float</td><td>degrees</td><td>Swing path angle relative to target; negative = out-to-in, positive = in-to-out</td></tr>
          <tr><td><code>SpeedAtImpact</code></td><td>float</td><td>mph or m/s</td><td>Club head speed measured precisely at moment of impact</td></tr>
          <tr><td><code>VerticalFaceImpact</code></td><td>float</td><td>normalized</td><td>Vertical face impact location; 0 = centre</td></tr>
          <tr><td><code>HorizontalFaceImpact</code></td><td>float</td><td>normalized</td><td>Horizontal face impact location; 0 = centre</td></tr>
          <tr><td><code>ClosureRate</code></td><td>float</td><td>deg/s</td><td>Rate at which the face is closing through impact</td></tr>
        </tbody>
      </table>

      <h3>ShotDataOptions fields</h3>
      <table class="api-table">
        <thead><tr><th>Field</th><th>Type</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>ContainsBallData</code></td><td>boolean</td><td>Set to <code>true</code> if the <code>BallData</code> object is populated and should be processed</td></tr>
          <tr><td><code>ContainsClubData</code></td><td>boolean</td><td>Set to <code>true</code> if the <code>ClubData</code> object is populated and should be processed</td></tr>
          <tr><td><code>LaunchMonitorIsReady</code></td><td>boolean</td><td>Whether the device is armed and ready to capture the next shot</td></tr>
          <tr><td><code>LaunchMonitorBallDetected</code></td><td>boolean</td><td>Whether the device has detected a ball in the hitting area</td></tr>
          <tr><td><code>IsHeartBeat</code></td><td>boolean</td><td>When <code>true</code> this message carries no shot data — it is a keep-alive ping. GSPro responds with code <code>202</code>.</td></tr>
        </tbody>
      </table>

      <h3>Example shot message (ProTee VX)</h3>
      <pre><code>{
  "DeviceID": "ProTee VX",
  "Units": "Yards",
  "ShotNumber": 12,
  "APIversion": "1",
  "BallData": {
    "Speed": 167.4,
    "SpinAxis": -8.2,
    "TotalSpin": 2614,
    "BackSpin": 2588,
    "SideSpin": -215,
    "HLA": -1.8,
    "VLA": 10.6
  },
  "ClubData": {
    "Speed": 112.3,
    "AngleOfAttack": -1.4,
    "FaceToTarget": -0.9,
    "Lie": 0.0,
    "Loft": 9.8,
    "Path": -1.5,
    "SpeedAtImpact": 111.9,
    "VerticalFaceImpact": 0.0,
    "HorizontalFaceImpact": 0.0,
    "ClosureRate": 0.0
  },
  "ShotDataOptions": {
    "ContainsBallData": true,
    "ContainsClubData": true,
    "LaunchMonitorIsReady": true,
    "LaunchMonitorBallDetected": true,
    "IsHeartBeat": false
  }
}</code></pre>

      <h2>Message Direction: GSPro → Launch Monitor</h2>
      <p>GSPro sends a response after every message it receives. The response carries a status code, an optional human-readable message, and — when the player's club or handedness changes — updated player state.</p>

      <h3>Response fields</h3>
      <table class="api-table">
        <thead><tr><th>Field</th><th>Type</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>Code</code></td><td>integer</td><td>Status code (see table below)</td></tr>
          <tr><td><code>Message</code></td><td>string</td><td>Human-readable description of the status</td></tr>
          <tr><td><code>Player</code></td><td>object</td><td>Current player state; present on connect and whenever club or handedness changes</td></tr>
          <tr><td><code>ShotNumber</code></td><td>integer</td><td>Echo of the <code>ShotNumber</code> from the incoming message</td></tr>
        </tbody>
      </table>

      <h3>Response codes</h3>
      <table class="api-table">
        <thead><tr><th>Code</th><th>Meaning</th></tr></thead>
        <tbody>
          <tr><td><code>200</code></td><td>Connected — sent immediately when GSPro accepts the TCP connection</td></tr>
          <tr><td><code>201</code></td><td>Shot received and accepted</td></tr>
          <tr><td><code>202</code></td><td>Heartbeat acknowledged</td></tr>
          <tr><td><code>501</code></td><td>Error — the incoming message was malformed or missing required fields</td></tr>
        </tbody>
      </table>

      <h3>Player object fields</h3>
      <table class="api-table">
        <thead><tr><th>Field</th><th>Type</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>Handedness</code></td><td>string</td><td><code>"RH"</code> (right-handed) or <code>"LH"</code> (left-handed)</td></tr>
          <tr><td><code>Club</code></td><td>string</td><td>Current club code (see club table below)</td></tr>
        </tbody>
      </table>

      <h3>Club codes</h3>
      <table class="api-table">
        <thead><tr><th>Code</th><th>Club</th><th>Code</th><th>Club</th></tr></thead>
        <tbody>
          <tr><td><code>DR</code></td><td>Driver</td><td><code>6I</code></td><td>6-Iron</td></tr>
          <tr><td><code>3W</code></td><td>3-Wood</td><td><code>7I</code></td><td>7-Iron</td></tr>
          <tr><td><code>5W</code></td><td>5-Wood</td><td><code>8I</code></td><td>8-Iron</td></tr>
          <tr><td><code>7W</code></td><td>7-Wood</td><td><code>9I</code></td><td>9-Iron</td></tr>
          <tr><td><code>2I</code></td><td>2-Iron</td><td><code>PW</code></td><td>Pitching Wedge</td></tr>
          <tr><td><code>3I</code></td><td>3-Iron</td><td><code>GW</code></td><td>Gap Wedge</td></tr>
          <tr><td><code>4I</code></td><td>4-Iron</td><td><code>SW</code></td><td>Sand Wedge</td></tr>
          <tr><td><code>5I</code></td><td>5-Iron</td><td><code>LW</code></td><td>Lob Wedge</td></tr>
          <tr><td><code>PT</code></td><td>Putter</td><td></td><td></td></tr>
        </tbody>
      </table>

      <h3>Example response (on connect)</h3>
      <pre><code>{
  "Code": 200,
  "Message": "GSPro Connected. Hello.",
  "Player": {
    "Handedness": "RH",
    "Club": "DR"
  },
  "ShotNumber": 0
}</code></pre>

      <h3>Example response (shot accepted)</h3>
      <pre><code>{
  "Code": 201,
  "Message": "Shot received.",
  "Player": {
    "Handedness": "RH",
    "Club": "7I"
  },
  "ShotNumber": 12
}</code></pre>

      <h2>ProTee VX Specifics</h2>
      <p>ProTee VX provides both ball data and club data, making it one of the more data-rich sources available. A few things worth knowing when integrating:</p>
      <ul>
        <li><strong>Unit output:</strong> ProTee VX reports in yards by default. Set <code>Units: "Yards"</code> in every message you send.</li>
        <li><strong>Spin convention:</strong> <code>SideSpin</code> is negative for draw and positive for fade. <code>SpinAxis</code> follows the same sign convention.</li>
        <li><strong>Club data availability:</strong> Club head data is only available on shots where the device captures an impact event — not on all practice swings or misreads. Always check <code>ShotDataOptions.ContainsClubData</code> before consuming <code>ClubData</code> fields.</li>
        <li><strong>Ball detection latency:</strong> there is a short delay between ball placement and <code>LaunchMonitorBallDetected</code> becoming <code>true</code>. Poll or subscribe to this flag before arming your shot-capture pipeline.</li>
        <li><strong>Heartbeat interval:</strong> ProTee VX's adapter typically sends a heartbeat every 2 seconds when no shot has been fired. Treat absence of heartbeats for more than ~5 seconds as a connectivity warning.</li>
      </ul>

      <h2>Integration Patterns</h2>
      <p><strong>Adapter layer.</strong> Wrap ProTee VX (or any monitor) behind a thin adapter interface that produces a canonical shot record. This lets your game logic, analytics, and UI stay monitor-agnostic and simplifies adding a second device later.</p>
      <p><strong>Shot correlation.</strong> Use <code>ShotNumber</code> as the correlation key between the outgoing message and GSPro's response. If you are logging or replaying traffic, this is the join field across both streams.</p>
      <p><strong>Reconnect strategy.</strong> The TCP connection can drop if GSPro restarts or the network hiccups. Implement exponential backoff reconnection (e.g. 1 s, 2 s, 4 s, 8 s) and re-send the last known <code>ShotDataOptions</code> state after reconnect so GSPro has current monitor status immediately.</p>
      <p><strong>Validation before sending.</strong> Reject or flag shots where <code>VLA</code> is below 0° or <code>Speed</code> is 0 — both indicate a misread. Sending bad data results in GSPro simulating an unintended shot that is difficult to explain to the end user.</p>
      <p><strong>Spy mode.</strong> For diagnostics, a passive TCP proxy sitting between the monitor and GSPro can log both directions of traffic without modifying payloads. This is the foundation of intercept-mode tooling and is especially useful when tracking down timing issues between monitor events and in-sim ball flight.</p>
    `,
  },
  {
    slug: 'gspro-api-primer',
    title: 'GSPro API Primer',
    summary: 'A practical overview of GSPro traffic, event flow, and where tooling can help.',
    published: '2026-02-27',
    html: `
      <p>GSPro publishes useful traffic that can be observed for diagnostics, integrations, and live overlays. For many workflows, the value is not just the raw payloads, but the timing between events and how those events line up with launch monitor telemetry.</p>
      <p>When building tools around GSPro, start by mapping your event timeline: pre-shot setup, shot trigger, ball-flight output, and score/state updates. A timeline view immediately reveals race conditions and missing fields that are hard to spot in raw logs.</p>
      <p>For device compatibility, design an adapter layer early. The adapter normalizes launch monitor data into one shared format so your higher-level logic can stay device-agnostic and easier to test.</p>
    `,
  },
  {
    slug: 'protee-vx-integration-notes',
    title: 'ProTee VX Integration Notes',
    summary: 'What to validate first when wiring ProTee VX traffic into analysis tooling.',
    published: '2026-02-27',
    html: `
      <p>ProTee VX support should begin with repeatability checks: timestamp consistency, unit normalization, and shot identity correlation between monitor events and GSPro updates.</p>
      <p>A robust integration pipeline typically includes three phases: capture, normalize, and classify. Capture preserves the raw stream, normalize converts fields to canonical units, and classify adds shot context for downstream features.</p>
      <p>Even if ProTee VX is your first target, keep monitor-specific logic behind interfaces. That lets future monitor support plug in without refactoring reporting, UI, or persistence layers.</p>
    `,
  },
];

export function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

function createMarketingHeader(activePath: string): string {
  const activeClass = (path: string) => (activePath === path ? ' active' : '');
  return `
    <header class="marketing-header">
      <div class="marketing-brand">
        <span class="brand-mark" aria-hidden="true">⛳</span>
        <div>
          <strong>GSPro Tools</strong>
          <span>Builder + Spy Suite</span>
        </div>
      </div>
      <nav class="marketing-nav" aria-label="Primary">
        <a class="marketing-nav-link${activeClass('/')}" href="/">Home</a>
        <a class="marketing-nav-link${activeClass('/products')}" href="/products">Products</a>
        <a class="marketing-nav-link${activeClass('/articles')}" href="/articles">Articles</a>
        <a class="marketing-nav-link${activeClass('/downloads')}" href="/downloads/index.html">Downloads</a>
        <a class="marketing-nav-link builder-cta" href="/builder">Launch Builder</a>
      </nav>
    </header>
  `;
}

function renderHomePage(app: HTMLElement): void {
  app.innerHTML = `
    ${createMarketingHeader('/')}
    <main class="marketing-main">
      <section class="hero-card">
        <p class="eyebrow">Professional GSPro Tooling</p>
        <h1>Build, analyze, and extend your simulator workflows.</h1>
        <p class="hero-copy">
          A focused suite of golf-simulation tools: a powerful browser-based course editor today, and a desktop traffic intelligence companion in progress.
        </p>
        <div class="hero-actions">
          <a class="btn btn-primary" href="/builder">Open GSPro Builder</a>
          <a class="btn btn-secondary" href="/products">Explore Products</a>
        </div>
      </section>

      <section class="marketing-grid">
        <article class="marketing-panel">
          <h2>GSPro Builder</h2>
          <p>Design and refine course layouts with a fast visual workflow and 3D preview support.</p>
          <a href="/builder">Go to Builder</a>
        </article>
        <article class="marketing-panel">
          <h2>GSPro Spy</h2>
          <p>A C# Windows desktop app (in development) for traffic visibility in <strong>spy</strong> and <strong>intercept</strong> modes.</p>
          <a href="/products#gspro-spy">View Details</a>
        </article>
        <article class="marketing-panel">
          <h2>Knowledge Base</h2>
          <p>Static, deep-linkable GSPro articles you can publish as part of each site deployment.</p>
          <a href="/articles">Read Articles</a>
        </article>
      </section>
    </main>
  `;
}

function renderProductsPage(app: HTMLElement): void {
  app.innerHTML = `
    ${createMarketingHeader('/products')}
    <main class="marketing-main">
      <section class="hero-card compact">
        <p class="eyebrow">Product Lineup</p>
        <h1>Focused tools for serious sim users.</h1>
      </section>
      <section class="marketing-grid">
        <article class="marketing-panel" id="gspro-builder">
          <h2>GSPro Builder</h2>
          <p>Web-based course construction and layout tooling with precision drawing controls and editing workflows.</p>
          <p>Use it in-browser for rapid iteration and export ready JSON artifacts.</p>
          <a href="/builder">Launch Builder</a>
        </article>
        <article class="marketing-panel" id="gspro-spy">
          <h2>GSPro Spy</h2>
          <p>An installable Windows proxy that sits between your launch monitor and GSPro, giving you full visibility into the data flowing through that connection — and the ability to alter it in real time.</p>
          <ul class="marketing-list">
            <li><strong>Spy mode:</strong> passively observe every payload exchanged between your launch monitor and GSPro.</li>
            <li><strong>Intercept mode:</strong> inspect, edit, and re-send data in-flight before it reaches GSPro.</li>
            <li><strong>Launch monitor support:</strong> initial target is ProTee VX, with an adapter-ready path for additional monitors.</li>
          </ul>
          <a href="/downloads/index.html">Downloads</a>
        </article>
      </section>
    </main>
  `;
}

function renderArticleListPage(app: HTMLElement): void {
  const cards = ARTICLES.map(
    (article) => `
      <article class="marketing-panel">
        <p class="article-date">${article.published}</p>
        <h2>${article.title}</h2>
        <p>${article.summary}</p>
        <a href="/articles/${article.slug}">Read Article</a>
      </article>
    `
  ).join('');
  app.innerHTML = `
    ${createMarketingHeader('/articles')}
    <main class="marketing-main">
      <section class="hero-card compact">
        <p class="eyebrow">GSPro Articles</p>
        <h1>Guides, references, and technical deep-dives for GSPro and launch monitor tooling.</h1>
      </section>
      <section class="marketing-grid">${cards}</section>
    </main>
  `;
}

function renderArticlePage(app: HTMLElement, slug: string): void {
  const article = ARTICLES.find((item) => item.slug === slug);
  if (!article) {
    app.innerHTML = `
      ${createMarketingHeader('/articles')}
      <main class="marketing-main">
        <section class="hero-card compact">
          <p class="eyebrow">Article</p>
          <h1>Not found</h1>
          <p>The requested article does not exist.</p>
          <div class="hero-actions">
            <a class="btn btn-secondary" href="/articles">Back to Articles</a>
          </div>
        </section>
      </main>
    `;
    return;
  }
  app.innerHTML = `
    ${createMarketingHeader('/articles')}
    <main class="marketing-main">
      <article class="hero-card article-card">
        <p class="article-date">${article.published}</p>
        <h1>${article.title}</h1>
        <p class="hero-copy">${article.summary}</p>
        <div class="article-content">${article.html}</div>
        <div class="hero-actions">
          <a class="btn btn-secondary" href="/articles">Back to Articles</a>
          <a class="btn btn-primary" href="/builder">Open Builder</a>
        </div>
      </article>
    </main>
  `;
}

export function initMarketing(pathname: string): void {
  const app = document.getElementById('app');
  if (!app) return;
  app.className = 'marketing-app';

  if (pathname === '/') {
    document.title = 'GSPro Tools | Home';
    renderHomePage(app);
    return;
  }
  if (pathname === '/products') {
    document.title = 'GSPro Tools | Products';
    renderProductsPage(app);
    return;
  }
  if (pathname === '/articles') {
    document.title = 'GSPro Tools | Articles';
    renderArticleListPage(app);
    return;
  }
  if (pathname.startsWith('/articles/')) {
    const slug = pathname.slice('/articles/'.length);
    document.title = 'GSPro Tools | Article';
    renderArticlePage(app, slug);
    return;
  }
  if (pathname === '/downloads' || pathname === '/downloads/') {
    window.location.replace('/downloads/index.html');
    return;
  }

  document.title = 'GSPro Tools | Home';
  renderHomePage(app);
}
